import { capitalize, Stack, styled, Typography } from '@mui/material';
import { type LogRecord } from '@repo/api-gw-sdk';

import { MultiSelectFilter } from '@/components/queryBuilder/filters';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { PropertyType, type FilterProperty } from '@/types/advanceFilter';

import { LogTimeCell, severities, SeverityCircle } from './logTimeCell';

const HighlightMark = styled('mark')({
  backgroundColor: 'var(--mui-palette-warning-bgLightHover)',
  borderRadius: '2px',
});

const timestamp: FilterProperty<LogRecord> = {
  name: 'timestamp',
  displayName: 'Timestamp (UTC)',
  type: PropertyType.String,
  group: '',
  column: {
    size: 220,
    cell: ({ row }) =>
      row.original.timestamp && <LogTimeCell entry={row.original} />,
  },
};

const raw: FilterProperty<LogRecord> = {
  name: 'raw',
  displayName: 'Raw',
  type: PropertyType.String,
  group: '',
  column: {
    size: 480,
    cell: ({ row }) => {
      const text = row.original.highlightedRaw || row.original.raw;
      if (!text) return null;
      if (!row.original.highlightedRaw) {
        return (
          <Typography noWrap component='div'>
            {text}
          </Typography>
        );
      }
      const parts = text.split(/\0HL_START\0(.*?)\0HL_END\0/g);
      return (
        <Typography noWrap component='div'>
          {parts.map((part, i) =>
            i % 2 === 1 ? (
              <HighlightMark key={i}>{part}</HighlightMark>
            ) : (
              <span key={i}>{part}</span>
            )
          )}
        </Typography>
      );
    },
  },
};

const sourcePath: FilterProperty<LogRecord> = {
  name: 'sourcePath',
  displayName: 'Source Path',
  type: PropertyType.String,
  group: '',
  accessorFn: (row) => row.sourcePath,
  column: {
    cell: ({ row }) =>
      row.original.sourcePath && (
        <OverflowTooltip text={row.original.sourcePath} reverseDirection />
      ),
  },
  field: {
    Component: ({ entity }) =>
      entity.sourcePath && <Typography>{entity.sourcePath}</Typography>,
  },
};

const level: FilterProperty<LogRecord> = {
  name: '.level',
  displayName: 'Level',
  type: PropertyType.String,
  group: '',
  accessorFn: (row) => row.level,
  column: {
    enableHiding: false,
    size: 120,
  },
  field: {
    Component: ({ entity }) => {
      return (
        entity.level && (
          <Stack direction='row' alignItems='center' gap='12px'>
            <SeverityCircle severity={entity.level} />
            <Typography>
              {capitalize(entity.level.toLocaleLowerCase())}
            </Typography>
          </Stack>
        )
      );
    },
  },
  filter: {
    expandedByDefault: true,
    Component: (props) => (
      <MultiSelectFilter
        {...props}
        options={Object.keys(severities).map((id) => ({
          id,
          name: capitalize(id.toLocaleLowerCase()),
          Image: <SeverityCircle severity={id} />,
        }))}
      />
    ),
  },
};

export const logSpaceSummaryFields: FilterProperty<LogRecord>[] = [
  level,
  sourcePath,
];
export const defaultColumns: FilterProperty<LogRecord>[] = [
  level,
  timestamp,
  raw,
  sourcePath,
];

const defaultColumnNames = new Set(defaultColumns.map((c) => c.name));

export const generateColumns = (logs: LogRecord[]) =>
  logs
    .reduce<string[]>((acc, log) => {
      Object.keys(log.fields || {}).forEach((key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
      });
      return acc;
    }, [])
    .sort()
    .filter(
      (x) => !defaultColumnNames.has(x) && x !== level.name.replace('.', '')
    )
    .map(
      (name) =>
        ({
          name,
          displayName: name,
          type: PropertyType.String,
          group: '',
          column: {
            size: 200,
            cell: ({ row }) => {
              const value = row.original.fields?.[name];
              if (value === undefined || value === null) {
                return null;
              }
              return (
                <Typography noWrap component='div'>
                  {typeof value === 'string' ? value : String(value)}
                </Typography>
              );
            },
          },
          field: {
            Component: ({ entity }) => {
              const value = entity.fields?.[name];
              if (value === undefined || value === null) {
                return null;
              }
              return (
                <Typography>
                  {typeof value === 'string' ? value : String(value)}
                </Typography>
              );
            },
          },
        }) satisfies FilterProperty<LogRecord>
    );
